import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Button,
  CheckBox,
  MinusCircleIcon,
  PlusCircleIcon,
  PlusIcon,
  Switch,
  Tabs,
  TrashIcon,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import type { FeeType } from "@dealer-center/ddc-pages-fee-manager-interface";
import {
  useBatchDeleteFees,
  useBatchUpdateInclude,
  useCreateFee,
  useDeleteFee,
  useFeeManagementStatus,
  useFees,
  useResetMockData,
  useToggleFeeManagement,
  useUpdateFee,
  useVersion
} from "@dealer-center/ddc-pages-fee-manager-fetch";
import { useFeeState } from "./hooks/useFeeState";
import { FeeRow } from "./components/FeeRow";
import { FeeRowSkeleton } from "./components/FeeRowSkeleton";
import { TotalFeesDisplay } from "./components/TotalFeesDisplay";
import { AddFeeModal } from "./components/AddFeeModal";
import { ConfirmationModal } from "./components/ConfirmationModal";
import {
  ButtonGroup,
  CursorTooltip,
  DebugButtonContainer,
  EnableBar,
  EnableStatusText,
  HeaderCell,
  InstructionText,
  LoadingOverlay,
  LoadingSpinnerContainer,
  PageContainer,
  PageTitle,
  SwitchWrapper,
  TableActionsRow,
  TableBody,
  TableContainer,
  TableHeader,
  TabsContainer
} from "./styles/FeeManager.styles";
import LABELS from "./labels.json";

export const FeeManager = () => {
  const navigate = useNavigate();
  const { toastError } = useToaster();
  const { userEntity, ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();
  const { "fee-manager-access": isFeeManagerEnabled } = useCC9Flags();

  const accountId = ccUserInfo?.accountId || "defaultAccount";
  const [activeTab, setActiveTab] = useState<FeeType>("new");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    feeNames: string[];
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    feeNames: [],
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onConfirm: () => {}
  });
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    text: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    text: ""
  });

  // Initialize locale labels once on mount
  useEffect(() => {
    Locale.setLabelsOnce(LABELS);
  }, []);

  // Fetch data
  const { data: versionData } = useVersion();

  useEffect(() => {
    if (versionData) {
      // eslint-disable-next-line no-console
      console.log("[FeeManager] version:", versionData.version);
    }
  }, [versionData]);

  const { data: statusData } = useFeeManagementStatus({ accountId });
  const {
    data: newFeesData,
    isLoading: newFeesLoading,
    refetch: refetchNewFees
  } = useFees({ accountId, type: "new" });
  const {
    data: preownedFeesData,
    isLoading: preownedFeesLoading,
    refetch: refetchPreownedFees
  } = useFees({ accountId, type: "preowned" });

  // Local state management
  const newFeeState = useFeeState(newFeesData?.fees || []);
  const preownedFeeState = useFeeState(preownedFeesData?.fees || []);

  // Destructure setFees to avoid object path references in effects
  const { setFees: setNewFees } = newFeeState;
  const { setFees: setPreownedFees } = preownedFeeState;

  const currentFeeState = activeTab === "new" ? newFeeState : preownedFeeState;
  const isEnabled =
    activeTab === "new" ? statusData?.new : statusData?.preowned;
  const isLoading = activeTab === "new" ? newFeesLoading : preownedFeesLoading;
  const isEditingAnyFee = currentFeeState.isAnyFeeBeingEdited();

  // Mutations
  const createFeeMutation = useCreateFee({ accountId });
  const updateFeeMutation = useUpdateFee({ accountId });
  const deleteFeeMutation = useDeleteFee({ accountId });
  const batchDeleteFeesMutation = useBatchDeleteFees({ accountId });
  const batchUpdateIncludeMutation = useBatchUpdateInclude({ accountId });
  const toggleManagementMutation = useToggleFeeManagement({ accountId });
  const resetMockDataMutation = useResetMockData({ accountId });

  // Track if any mutation is in progress
  const isMutating =
    createFeeMutation.isPending ||
    updateFeeMutation.isPending ||
    deleteFeeMutation.isPending ||
    batchDeleteFeesMutation.isPending ||
    batchUpdateIncludeMutation.isPending ||
    toggleManagementMutation.isPending ||
    resetMockDataMutation.isPending;

  // Update local state when data changes
  useEffect(() => {
    if (newFeesData?.fees) {
      setNewFees(newFeesData.fees);
    }
  }, [newFeesData, setNewFees]);

  useEffect(() => {
    if (preownedFeesData?.fees) {
      setPreownedFees(preownedFeesData.fees);
    }
  }, [preownedFeesData, setPreownedFees]);

  useEffect(() => {
    if (isFeeManagerEnabled === false && !userEntity?.internal) {
      navigate("/");
    }
  }, [isFeeManagerEnabled, userEntity, navigate]);

  // Handlers
  const handleToggleEnable = () => {
    if (isMutating) return;
    const currentEnabled = isEnabled;
    toggleManagementMutation.mutate(
      {
        type: activeTab,
        enabled: !isEnabled
      },
      {
        onError: (error: Error) => {
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_TOGGLE_TITLE"))}: ${
              error.message || "Failed to toggle fee management"
            }`
          );
        }
      }
    );
  };

  const handleToggleIncludeAll = () => {
    if (isMutating) return;
    const allIncluded = currentFeeState.fees.every(f => f.include);
    const feeIds = currentFeeState.fees.map(f => f.id);
    batchUpdateIncludeMutation.mutate(
      {
        feeIds,
        include: !allIncluded,
        type: activeTab
      },
      {
        onError: (error: Error) => {
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_UPDATE_TITLE"))}: ${
              error.message || "Failed to update fees"
            }`
          );
        }
      }
    );
  };

  const handleIncludeSelected = () => {
    if (isMutating) return;
    const selected = currentFeeState.getSelectedFees();
    if (selected.length === 0) return;
    const feeIds = selected.map(f => f.id);
    batchUpdateIncludeMutation.mutate(
      {
        feeIds,
        include: true,
        type: activeTab
      },
      {
        onSuccess: () => {
          currentFeeState.clearSelection();
        },
        onError: (error: Error) => {
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_UPDATE_TITLE"))}: ${
              error.message || "Failed to include fees"
            }`
          );
        }
      }
    );
  };

  const handleExcludeSelected = () => {
    if (isMutating) return;
    const selected = currentFeeState.getSelectedFees();
    if (selected.length === 0) return;
    const feeIds = selected.map(f => f.id);
    batchUpdateIncludeMutation.mutate(
      {
        feeIds,
        include: false,
        type: activeTab
      },
      {
        onSuccess: () => {
          currentFeeState.clearSelection();
        },
        onError: (error: Error) => {
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_UPDATE_TITLE"))}: ${
              error.message || "Failed to exclude fees"
            }`
          );
        }
      }
    );
  };

  const handleToggleInclude = (feeId: string) => {
    if (isMutating) return;
    const fee = currentFeeState.fees.find(f => f.id === feeId);
    if (!fee) return;

    updateFeeMutation.mutate(
      {
        type: activeTab,
        feeId,
        include: !fee.include
      },
      {
        onError: (error: Error) => {
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_UPDATE_TITLE"))}: ${
              error.message || "Failed to update fee"
            }`
          );
        }
      }
    );
  };

  const handleAddFee = (data: {
    name: string;
    amount: number | null;
    include: boolean;
  }) => {
    if (isMutating) return;
    createFeeMutation.mutate(
      {
        type: activeTab,
        ...data
      },
      {
        onSuccess: () => {
          setIsAddModalOpen(false);
        },
        onError: (error: Error) => {
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_CREATE_TITLE"))}: ${
              error.message || "Failed to create fee"
            }`
          );
          // Keep modal open so user can retry
        }
      }
    );
  };

  const handleSaveEdit = (
    feeId: string,
    name: string,
    amount: number | null,
    include: boolean
  ) => {
    if (isMutating) return;
    // Save original values for rollback on error
    const originalFee = currentFeeState.fees.find(f => f.id === feeId);
    if (!originalFee) return;

    // Apply optimistic update immediately so user sees changes right away
    currentFeeState.applyOptimisticUpdate(feeId, { name, amount, include });

    // Send to server in background
    updateFeeMutation.mutate(
      {
        type: activeTab,
        feeId,
        name,
        amount,
        include
      },
      {
        onError: (error: Error) => {
          // Revert optimistic update
          currentFeeState.applyOptimisticUpdate(feeId, {
            name: originalFee.name,
            amount: originalFee.amount,
            include: originalFee.include
          });
          // Show error to user
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_UPDATE_TITLE"))}: ${
              error.message || "Failed to save changes"
            }`
          );
        }
      }
    );
  };

  const handleDeleteSelected = async () => {
    if (isMutating) return;
    const selected = currentFeeState.getSelectedFees();
    if (selected.length === 0) return;

    const names = selected.map(f => f.name);
    setConfirmationModal({
      isOpen: true,
      title: String(
        Locale.getText(
          selected.length > 1
            ? "FEEMANAGER_DELETECONF_BULK_TITLE"
            : "FEEMANAGER_DELETECONF_TITLE"
        )
      ),
      feeNames: names,
      onConfirm: async () => {
        try {
          // Use batch delete for multiple fees
          await batchDeleteFeesMutation.mutateAsync({
            type: activeTab,
            feeIds: selected.map(f => f.id)
          });
          currentFeeState.clearSelection();
        } catch (error) {
          toastError(
            `${String(Locale.getText("FEEMANAGER_ERROR_DELETE_TITLE"))}: ${
              error instanceof Error ? error.message : "Failed to delete fees"
            }`
          );
        } finally {
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleDeleteSingle = (feeId: string) => {
    const fee = currentFeeState.fees.find(f => f.id === feeId);
    if (!fee) return;

    setConfirmationModal({
      isOpen: true,
      title: String(Locale.getText("FEEMANAGER_DELETECONF_TITLE")),
      feeNames: [fee.name],
      onConfirm: () => {
        deleteFeeMutation.mutate(
          { type: activeTab, feeId },
          {
            onError: (error: Error) => {
              toastError(
                `${String(Locale.getText("FEEMANAGER_ERROR_DELETE_TITLE"))}: ${
                  error.message || "Failed to delete fee"
                }`
              );
            }
          }
        );
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const calculateTotal = () => {
    const sum = currentFeeState.fees
      .filter(f => f.include)
      .reduce((sum, f) => sum + (f.amount || 0), 0);
    // Round to 2 decimal places to handle floating point precision
    return Math.round(sum * 100) / 100;
  };

  const handleShowTooltip = (x: number, y: number, text: string) => {
    setTooltip({ visible: true, x, y, text });
  };

  const handleHideTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const allSelected =
    currentFeeState.fees.length > 0 &&
    currentFeeState.fees.every(f => f.selected);
  const allIncluded =
    currentFeeState.fees.length > 0 &&
    currentFeeState.fees.every(f => f.include);
  const selectedCount = currentFeeState.getSelectedFees().length;

  const enableStatusText = isEnabled
    ? Locale.getText(
        activeTab === "new"
          ? "FEEMANAGER_NEW_FEEMANAGE_ENABLED_TEXT"
          : "FEEMANAGER_PREOWNED_FEEMANAGE_ENABLED_TEXT"
      )
    : Locale.getText(
        activeTab === "new"
          ? "FEEMANAGER_NEW_FEEMANAGE_DISABLED_TEXT"
          : "FEEMANAGER_PREOWNED_FEEMANAGE_DISABLED_TEXT"
      );

  const tabs = [
    {
      label: String(Locale.getText("FEEMANAGER_NEW_TAB")),
      value: 0,
      active: activeTab === "new",
      disabled: isEditingAnyFee
    },
    {
      label: String(Locale.getText("FEEMANAGER_PREOWNED_TAB")),
      value: 1,
      active: activeTab === "preowned",
      disabled: isEditingAnyFee
    }
  ];

  const handleTabActivate = (index: number) => {
    if (isEditingAnyFee) return;
    setActiveTab(index === 0 ? "new" : "preowned");
  };

  return (
    <PageContainer>
      <PageTitle>
        <Typography variant={"h1"}>
          {String(Locale.getText("FEEMANAGER_TITLE"))}
        </Typography>
        <DebugButtonContainer>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            size="small"
            onClick={() => resetMockDataMutation.mutate()}
          >
            {String(Locale.getText("FEEMANAGER_RELOADMOCKDATA"))}
          </Button>
        </DebugButtonContainer>
      </PageTitle>

      <TabsContainer disabled={isEditingAnyFee}>
        <Tabs id="fee-tabs" tabs={tabs} onActivate={handleTabActivate} />
      </TabsContainer>

      {/* Enable/Disable Bar */}
      <EnableBar $enabled={isEnabled}>
        <SwitchWrapper
          disabled={isEditingAnyFee}
          onClick={e => {
            if (!isEditingAnyFee) {
              e.stopPropagation();
              handleToggleEnable();
            }
          }}
        >
          <Switch
            checked={isEnabled || false}
            disabled={isEditingAnyFee}
            label=""
          />
        </SwitchWrapper>
        <EnableStatusText>{enableStatusText}</EnableStatusText>
      </EnableBar>

      {/* Action Buttons */}
      <TableActionsRow>
        <ButtonGroup>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            disabled={!isEnabled || selectedCount === 0 || isEditingAnyFee}
            onClick={handleIncludeSelected}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <PlusCircleIcon
                style={{ width: "16px", height: "16px" }}
                title=""
              />
              {String(Locale.getText("FEEMANAGER_INCLUDEALL_SHORT"))}
            </span>
          </Button>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            disabled={!isEnabled || selectedCount === 0 || isEditingAnyFee}
            onClick={handleExcludeSelected}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <MinusCircleIcon
                style={{ width: "16px", height: "16px" }}
                title=""
              />
              {String(Locale.getText("FEEMANAGER_EXCLUDEALL_SHORT"))}
            </span>
          </Button>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            disabled={!isEnabled || selectedCount === 0 || isEditingAnyFee}
            onClick={handleDeleteSelected}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <TrashIcon style={{ width: "16px", height: "16px" }} title="" />
              {String(Locale.getText("FEEMANAGER_DELETE"))}
            </span>
          </Button>
        </ButtonGroup>
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="primary"
          disabled={!isEnabled || isEditingAnyFee}
          onClick={() => setIsAddModalOpen(true)}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <PlusIcon style={{ width: "16px", height: "16px" }} title="" />
            {String(Locale.getText("FEEMANAGER_ADDFEE"))}
          </span>
        </Button>
      </TableActionsRow>

      {/* Table */}
      <TableContainer onMouseLeave={handleHideTooltip}>
        {/* Instruction text */}
        <InstructionText>
          {String(Locale.getText("FEEMANAGER_CLICK_ENTRY_TO_EDIT"))}
        </InstructionText>

        {/* Table Header */}
        <TableHeader disabled={!isEnabled || isEditingAnyFee}>
          <HeaderCell $center>
            <CheckBox
              checked={allSelected}
              disabled={!isEnabled || isEditingAnyFee}
              value="select-all"
              onChange={() => currentFeeState.selectAll()}
            />
          </HeaderCell>
          <HeaderCell>{String(Locale.getText("FEEMANAGER_NAME"))}</HeaderCell>
          <HeaderCell>{/* New empty column */}</HeaderCell>
          <HeaderCell $center $shiftLeft>
            {String(Locale.getText("FEEMANAGER_INCLUDE_HEADER"))}
          </HeaderCell>
          <HeaderCell $shiftLeft>
            {String(Locale.getText("FEEMANAGER_AMOUNT"))}
          </HeaderCell>
          <HeaderCell>{/* Right spacing */}</HeaderCell>
        </TableHeader>

        {/* Table Body */}
        {isLoading ? (
          <TableBody disabled={true}>
            <FeeRowSkeleton count={5} />
          </TableBody>
        ) : (
          <TableBody disabled={!isEnabled}>
            {currentFeeState.fees.map(fee => (
              <FeeRow
                disabled={!isEnabled || (isEditingAnyFee && !fee.editing)}
                existingFees={currentFeeState.fees}
                fee={fee}
                key={fee.id}
                locale={Locale.getLocale()}
                // eslint-disable-next-line react/jsx-handler-names
                onCancelEdit={currentFeeState.exitEditMode}
                onDelete={handleDeleteSingle}
                // eslint-disable-next-line react/jsx-handler-names
                onEditClick={currentFeeState.enterEditMode}
                onHideTooltip={handleHideTooltip}
                onSaveEdit={handleSaveEdit}
                onShowTooltip={handleShowTooltip}
                onToggleInclude={handleToggleInclude}
                // eslint-disable-next-line react/jsx-handler-names
                onToggleSelection={currentFeeState.toggleSelection}
              />
            ))}
          </TableBody>
        )}

        {/* Total Fees Display */}
        <TotalFeesDisplay
          disabled={!isEnabled}
          locale={Locale.getLocale()}
          total={calculateTotal()}
          vehicleType={activeTab}
        />
      </TableContainer>

      {/* Modals */}
      <AddFeeModal
        existingFees={currentFeeState.fees}
        isOpen={isAddModalOpen}
        type={activeTab}
        onAdd={handleAddFee}
        onCancel={() => setIsAddModalOpen(false)}
      />

      {/* eslint-disable react/jsx-handler-names */}
      <ConfirmationModal
        confirmText={String(Locale.getText("FEEMANAGER_DELETE"))}
        confirmVariant="danger"
        feeNames={confirmationModal.feeNames}
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        onCancel={() =>
          setConfirmationModal(prev => ({ ...prev, isOpen: false }))
        }
        onConfirm={confirmationModal.onConfirm}
      />
      {/* eslint-enable react/jsx-handler-names */}

      {/* Global cursor-following tooltip */}
      <CursorTooltip $visible={tooltip.visible} $x={tooltip.x} $y={tooltip.y}>
        {tooltip.text}
      </CursorTooltip>

      {/* Loading overlay when mutations are in progress */}
      <LoadingOverlay $visible={isMutating}>
        <LoadingSpinnerContainer>
          <Typography variant="body-lg">
            {String(Locale.getText("FEEMANAGER_SAVING"))}
          </Typography>
        </LoadingSpinnerContainer>
      </LoadingOverlay>
    </PageContainer>
  );
};
